// Driver balance — what Stripe is holding for this driver, and every payment an admin
// has pushed into it.
//
// Was a `Paper` with two nested collapses: the whole section folded, and the payment log
// folded again inside it, so the log was two clicks from a page that had already been
// opened to look at it. Now a `SectionCard` with the figures on top and the log as
// hairline `Row`s — always open, like every other section on this page, and capped in
// height instead of hidden.
//
// Reverse belongs to the ROW — a section-header "Reverse" would have to ask which payment,
// and the row already knows — but it sits behind that row's overflow menu rather than
// printed on it. See `RowMenu`.
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Skeleton, Stack, Typography } from '../../../ui/core';
import { DollarBadgeIcon } from '../../../ui/icons';
import { hairline, scrollY } from '../../../ui/styles';
import { useFormatters } from '../../../hooks';
import { getDriverBalance, getDriverPaymentLog } from '../../../redux/Users/actions';
import { SectionCard, SectionLabel, EmptyRows } from '../../../pages/DriverProfile/components';
import {
  Row,
  Amount,
  Figure,
  Figures,
  TrailingMeta,
  Meta,
  IconAction,
  RowAction,
  RowMenu,
} from '../../../pages/UserProfile/parts';
import DriverBalanceAddPaymentDialog from './DriverBalanceAdjustDialog';
import DriverPaymentReverseDialog from './DriverPaymentReverseDialog';

function formatEnumLabel(value) {
  if (!value) {
    return '-';
  }

  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** An inline "couldn't load this" with its retry, row-shaped so it sits in the card. */
const LoadFailed = ({ children, onRetry }) => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 1.75, py: 1.5 }}>
    <Typography sx={{ fontSize: '12.5px', color: 'error.main', flex: 1 }}>{children}</Typography>
    <Button variant="outlined" color="inherit" size="small" onClick={onRetry} sx={{ textTransform: 'none' }}>
      Retry
    </Button>
  </Stack>
);

const DriverBalanceCard = ({ user }) => {
  const dispatch = useDispatch();
  const f = useFormatters();
  const balance = useSelector((state) => state.users.selection.driverBalance);
  const balanceError = useSelector((state) => state.users.selection.driverBalanceError);
  const paymentLog = useSelector((state) => state.users.selection.driverPaymentLog);
  const paymentLogError = useSelector((state) => state.users.selection.driverPaymentLogError);

  const [isAddPaymentOpen, setIsAddPaymentOpen] = useState(false);
  // The payment row currently being reversed, or null when the dialog is closed.
  const [reverseEntry, setReverseEntry] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    getDriverBalance(user.id)(dispatch);
    getDriverPaymentLog(user.id)(dispatch);
  }, [user.id, dispatch]);

  const isLoaded = !!balance;
  const isLogLoaded = !!paymentLog;
  const nextCursor = paymentLog?.nextCursor ?? null;

  // Fold reversals into the payment they reverse: only show issued payments.
  // Each carries its own reversal state (reversedCents / reversibleCents /
  // isFullyReversed), so a reversal never shows up as its own separate row.
  const payments = useMemo(
    () => (paymentLog?.entries ?? []).filter((entry) => (entry.amountCents ?? 0) > 0),
    [paymentLog],
  );

  async function refreshBalance() {
    await getDriverBalance(user.id)(dispatch);
  }

  async function refreshAll() {
    await Promise.all([getDriverBalance(user.id)(dispatch), getDriverPaymentLog(user.id)(dispatch)]);
  }

  async function loadMore() {
    if (!nextCursor) {
      return;
    }
    setIsLoadingMore(true);
    try {
      await getDriverPaymentLog(user.id, nextCursor)(dispatch);
    } finally {
      setIsLoadingMore(false);
    }
  }

  const lastUpdatedLabel =
    isLoaded && balance.updatedAt ? f.date.toLocaleString(balance.updatedAt, f.date.DateFormats.DATETIME_MED) : null;

  const pending = <Skeleton variant="text" width={72} />;

  return (
    <>
      <SectionCard
        title="Driver balance"
        icon={<DollarBadgeIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
        meta={
          <TrailingMeta>
            {/* When Stripe was last read, not when the driver was last paid — the
                difference matters when someone is asking why a number looks stale. */}
            {lastUpdatedLabel && <Meta>as of {lastUpdatedLabel}</Meta>}
            <IconAction
              kind="add"
              label="Add a payment"
              onClick={() => setIsAddPaymentOpen(true)}
              disabled={!isLoaded}
            />
          </TrailingMeta>
        }
      >
        {balanceError && !isLoaded ? (
          <LoadFailed onRetry={refreshBalance}>Couldn&rsquo;t load this driver&rsquo;s balance.</LoadFailed>
        ) : (
          <Figures>
            {/* Available is the one that answers "can they be paid right now", so it is
                the tinted figure; pending is the same money one state earlier. */}
            <Figure
              label="Available"
              value={isLoaded ? f.number.currency(balance.available ?? 0) : pending}
              palette="success"
            />
            <Figure label="Pending" value={isLoaded ? f.number.currency(balance.pending ?? 0) : pending} />
            <Figure
              label="Lifetime paid"
              value={isLoaded ? f.number.currency(balance.lifetimeCredited ?? 0) : pending}
            />
          </Figures>
        )}

        <Box sx={{ px: 1.75, pt: 1.25, pb: 0.5, borderTop: (t) => `1px solid ${hairline(t)}` }}>
          <SectionLabel>Payment log</SectionLabel>
        </Box>

        {!isLogLoaded && !paymentLogError && (
          <Stack spacing={0.5} sx={{ px: 1.75, pb: 1.5 }}>
            <Skeleton variant="rectangular" height={22} />
            <Skeleton variant="rectangular" height={22} />
          </Stack>
        )}

        {paymentLogError && !isLogLoaded && (
          <LoadFailed onRetry={() => getDriverPaymentLog(user.id)(dispatch)}>
            Couldn&rsquo;t load the payment log.
          </LoadFailed>
        )}

        {isLogLoaded && payments.length === 0 && <EmptyRows>No payments yet.</EmptyRows>}

        {isLogLoaded && payments.length > 0 && (
          <Box sx={{ maxHeight: 320, ...scrollY }}>
            {payments.map((entry, i) => {
              const amount = (entry.amountCents ?? 0) / 100;
              const reversedCents = entry.reversedCents ?? 0;
              const reversibleCents = entry.reversibleCents ?? 0;
              const isFullyReversed = !!entry.isFullyReversed;

              return (
                <Row
                  key={entry.id}
                  first={i === 0}
                  at={f.date.toLocaleString(entry.createdAt, f.date.DateFormats.DATE_SHORT)}
                  label={`${formatEnumLabel(entry.reason)} · ${entry.performedByAdminEmail || 'Unknown admin'}`}
                  meta={[
                    entry.description && `“${entry.description}”`,
                    // Partial reversals are the case worth spelling out: the amount on
                    // the row is still what was issued, and this is what is left of it.
                    reversedCents > 0 &&
                      `${f.number.currency(reversedCents / 100)} reversed${
                        !isFullyReversed && reversibleCents > 0
                          ? ` · ${f.number.currency(reversibleCents / 100)} remaining`
                          : ''
                      }`,
                    isFullyReversed && 'Fully reversed',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  right={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {/* Struck through when it is fully reversed, so the log still shows
                          the payment happened without it counting as money held. */}
                      <Box
                        sx={{
                          textDecoration: isFullyReversed ? 'line-through' : 'none',
                          opacity: isFullyReversed ? 0.6 : 1,
                        }}
                      >
                        <Amount value={`+${f.number.currency(amount)}`} />
                      </Box>
                      {/* Reverse moves behind the overflow. As a red word on the row it
                          was the loudest thing in a scrollable list of a driver's whole
                          payment history — an irreversible money action sitting one
                          mis-click from the amount it would undo, repeated on every row.
                          Same treatment as Payment sources' delete. */}
                      <RowMenu
                        disabled={reversibleCents <= 0}
                        label={`Actions for the ${f.number.currency(amount)} payment`}
                        items={[
                          {
                            label: 'Reverse payment',
                            danger: true,
                            onClick: () => setReverseEntry(entry),
                          },
                        ]}
                      />
                    </Stack>
                  }
                />
              );
            })}
          </Box>
        )}

        {nextCursor && (
          <Box sx={{ px: 1.75, py: 1, borderTop: (t) => `1px solid ${hairline(t)}` }}>
            <RowAction onClick={loadMore} disabled={isLoadingMore}>
              {isLoadingMore ? 'Loading…' : 'Load more'}
            </RowAction>
          </Box>
        )}
      </SectionCard>

      {isLoaded && (
        <DriverBalanceAddPaymentDialog
          isOpen={isAddPaymentOpen}
          onClose={() => setIsAddPaymentOpen(false)}
          onAdjusted={refreshAll}
          user={user}
          currentBalance={balance.available ?? 0}
        />
      )}

      <DriverPaymentReverseDialog
        isOpen={!!reverseEntry}
        entry={reverseEntry}
        onClose={() => setReverseEntry(null)}
        onReversed={refreshAll}
        user={user}
      />
    </>
  );
};

export default DriverBalanceCard;
